import { useGtm } from '@gtm-support/vue-gtm'
import { useTracking } from '@/composables/tracking'
import { initializeSentry, teardownSentry } from '@/plugins/sentry'
import { onBeforeMount, onUnmounted } from 'vue'

/**
 * Represents the consent data received via postMessage from the host page.
 * @property analytics - Whether the user has given consent for analytics tracking.
 */
export type DataConsent = {
  analytics?: boolean
}

/**
 * Composable that registers window message listeners for cross-origin communication
 * when the app is embedded as an iframe. Initializes listeners during the `onBeforeMount` lifecycle hook
 * and removes them during the `onUnmounted` lifecycle hook.
 */
export const useMessageListeners = () => {
  /**
   * Applies host-page analytics consent to GTM, Sentry, and consent tracking.
   * @param consent - The consent data payload from the host page.
   */
  const handleConsent = (consent: DataConsent) => {
    if (!consent?.analytics) console.warn('Could not enable analytics')
    const hasAnalyticsConsent = consent?.analytics === true
    useGtm().enable(hasAnalyticsConsent)
    if (hasAnalyticsConsent) initializeSentry()
    else teardownSentry()
    useTracking().partnerConsentUpdate(hasAnalyticsConsent)
  }

  /**
   * Handles an iframe visibility message by triggering the mini variant view tracking.
   */
  const handleIframeVisible = () => {
    useTracking().viewMini()
  }

  const messageEventHandler = ({ data }: MessageEvent) => {
    if (!!data.type)
      switch (data.type) {
        case 'consent':
          handleConsent(data.data ?? {})
          break
        case 'iframe_visible':
          handleIframeVisible()
          break
      }
  }

  onBeforeMount(() => {
    window.addEventListener('message', messageEventHandler)
  })

  onUnmounted(() => window.removeEventListener('message', messageEventHandler))
}
